import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  alpha,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import StarIcon from "@mui/icons-material/Star";
import Layout from "../components/layout/LandingPageLayout";
import axios from "axios";

export const SubscriptionPlan = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const plans = [
    {
      name: "Basic",
      icon: <RocketLaunchIcon sx={{ fontSize: 40 }} />,
      tagline: "Perfect for startups and small businesses",
      monthlyPrice: 10000,
      setupFee: 50000,
      popular: false,
      features: [
        "50 Transactions & Invoices per month",
        "Basic bookkeeping",
        "Monthly financial reports",
        "Email support",
        "Single user access",
        "Cloud storage (5GB)",
        "Mobile app access",
        "Valid for 12 months",
      ],
      transactionLimit: "50 Transactions and Invoices",
      color: theme.palette.primary.light,
    },
    {
      name: "Silver",
      icon: <BusinessCenterIcon sx={{ fontSize: 40 }} />,
      tagline: "Most popular for growing businesses",
      monthlyPrice: 15000,
      setupFee: 100000,
      popular: true,
      features: [
        "200 Transactions & Invoices per month",
        "Advanced bookkeeping & accounting",
        "Weekly financial reports",
        "Priority email & chat support",
        "Up to 5 user accounts",
        "Cloud storage (50GB)",
        "Mobile & desktop app access",
        "Tax planning assistance",
        "Payroll processing (up to 10 employees)",
        "Real-time data sync",
        "Valid for 12 months",
      ],
      transactionLimit: "200 Transactions and Invoices",
      color: theme.palette.primary.main,
    },
    {
      name: "Gold",
      icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
      tagline: "For established and scaling enterprises",
      monthlyPrice: 360000,
      setupFee: 300000,
      popular: false,
      features: [
        "750 Transactions & Invoices per month",
        "Everything in Silver",
        "Daily financial reports & analytics",
        "24/7 dedicated support",
        "Unlimited cloud storage",
        "Multi-location support",
        "Advanced tax optimization",
        "Unlimited payroll processing",
        "Custom integrations (ERP/CRM)",
        "Compliance management",
        "Dedicated account manager",
        "API access",
        "Valid for 12 months",
      ],
      transactionLimit: "750 Transactions and Invoices",
      color: theme.palette.primary.dark,
    },
  ];

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(price);
  };

  const handleSubscribe = async (plan) => {
    setLoading(true);
    setSelectedPlan(plan.name);

    try {
      // Step 1: Send only plan identifier to backend - backend will calculate everything
      const response = await axios.post("/api/subscriptions/create-order", {
        planId: plan.name.toLowerCase(), // Only send plan identifier
      });

      // If order creation is successful, initiate Razorpay payment
      if (response.data.success) {
        const { orderId, amount, currency, razorpayKeyId } = response.data;

        // Initialize Razorpay payment with backend-generated values
        const options = {
          key: razorpayKeyId, // Key sent from backend for security
          amount: amount, // Amount calculated on backend
          currency: currency, // Currency set on backend
          name: "f-ai",
          description: `${plan.name} Plan Subscription`,
          order_id: orderId, // Server-generated order ID
          handler: async function (razorpayResponse) {
            // Step 2: Payment success - send only Razorpay response to backend for verification
            try {
              const verifyResponse = await axios.post(
                "/api/subscriptions/verify-payment",
                {
                  razorpayPaymentId: razorpayResponse.razorpay_payment_id,
                  razorpayOrderId: razorpayResponse.razorpay_order_id,
                  razorpaySignature: razorpayResponse.razorpay_signature,
                  // Backend will verify amount and activate subscription based on order ID
                }
              );

              if (verifyResponse.data.success) {
                setSnackbar({
                  open: true,
                  message: "Subscription activated successfully!",
                  severity: "success",
                });
                // Redirect to dashboard or subscription success page after delay
                setTimeout(() => {
                  window.location.href = "/dashboard";
                }, 2000);
              }
            } catch (error) {
              setSnackbar({
                open: true,
                message: "Payment verification failed. Please contact support.",
                severity: "error",
              });
              console.error("Payment verification error:", error);
            }
          },
          prefill: {
            name: "", // Get from user context/profile
            email: "", // Get from user context/profile
            contact: "", // Get from user context/profile
          },
          theme: {
            color: theme.palette.primary.main,
          },
          modal: {
            ondismiss: function () {
              setLoading(false);
              setSelectedPlan(null);
              setSnackbar({
                open: true,
                message: "Payment cancelled",
                severity: "info",
              });
            },
          },
        };

        const razorpay = new window.Razorpay(options);
        razorpay.open();

        razorpay.on("payment.failed", function (response) {
          setSnackbar({
            open: true,
            message: `Payment failed: ${response.error.description}`,
            severity: "error",
          });
          setLoading(false);
          setSelectedPlan(null);
        });
      }
    } catch (error) {
      console.error("Subscription error:", error);
      setSnackbar({
        open: true,
        message:
          error.response?.data?.message ||
          "Failed to create subscription. Please try again.",
        severity: "error",
      });
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Layout>
      <Box
        sx={{
          py: 8,
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.03
          )} 0%, ${alpha(theme.palette.primary.light, 0.08)} 100%)`,
          minHeight: "100vh",
        }}
      >
        <Container maxWidth="xl">
          {/* Header Section */}
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h2"
              component="h1"
              gutterBottom
              align="center"
              sx={{
                fontWeight: 700,
                color: theme.palette.primary.main,
                mb: 2,
              }}
            >
              Choose Your Plan
            </Typography>
            <Typography
              variant="h6"
              align="center"
              color="text.secondary"
              sx={{ mb: 4, maxWidth: 700, mx: "auto" }}
            >
              Start your journey with f-ai. Professional plans designed to scale
              with your business needs.
            </Typography>

            {/* Early Bird Banner */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                mb: 4,
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.warning.main, 0.1),
                border: `2px solid ${theme.palette.warning.main}`,
                maxWidth: 600,
                mx: "auto",
              }}
            >
              <StarIcon sx={{ color: theme.palette.warning.main }} />
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.warning.dark,
                }}
              >
                🎉 Early Adopter Special: Get 50% off for the first 3 months!
              </Typography>
            </Box>
          </Box>

          {/* Plans Grid */}
          <Grid container spacing={4}>
            {plans.map((plan, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    position: "relative",
                    borderRadius: 3,
                    border: plan.popular
                      ? `3px solid ${theme.palette.primary.main}`
                      : `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    background: plan.popular
                      ? `linear-gradient(135deg, ${alpha(
                          theme.palette.primary.main,
                          0.05
                        )} 0%, ${alpha(theme.palette.primary.light, 0.1)} 100%)`
                      : theme.palette.background.paper,
                    overflow: "visible",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-12px)",
                      boxShadow: theme.shadows[20],
                    },
                  }}
                >
                  {plan.popular && (
                    <Chip
                      label="MOST POPULAR"
                      sx={{
                        position: "absolute",
                        top: -15,
                        left: "50%",
                        transform: "translateX(-50%)",
                        bgcolor: theme.palette.primary.main,
                        color: "white",
                        fontWeight: 700,
                        px: 3,
                        py: 2.5,
                        fontSize: "0.75rem",
                      }}
                    />
                  )}

                  <CardContent sx={{ p: 4, pt: plan.popular ? 5 : 4 }}>
                    {/* Icon */}
                    <Box
                      sx={{
                        display: "inline-flex",
                        p: 2,
                        borderRadius: 2,
                        bgcolor: alpha(plan.color, 0.1),
                        color: plan.color,
                        mb: 2,
                      }}
                    >
                      {plan.icon}
                    </Box>

                    {/* Plan Name */}
                    <Typography
                      variant="h4"
                      component="h3"
                      gutterBottom
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.primary.dark,
                      }}
                    >
                      {plan.name}
                    </Typography>

                    {/* Tagline */}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 3, minHeight: 40 }}
                    >
                      {plan.tagline}
                    </Typography>

                    {/* Price */}
                    <Box sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "baseline",
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="h3"
                          component="span"
                          sx={{
                            fontWeight: 700,
                            color: theme.palette.primary.main,
                          }}
                        >
                          {formatPrice(plan.monthlyPrice)}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          /month
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          mt: 1,
                          fontWeight: 500,
                        }}
                      >
                        + {formatPrice(plan.setupFee)} Setup Fee
                      </Typography>
                    </Box>

                    {/* Transaction Limit Badge */}
                    <Box
                      sx={{
                        mb: 3,
                        p: 1.5,
                        bgcolor: alpha(theme.palette.success.main, 0.1),
                        borderRadius: 2,
                        border: `1px solid ${alpha(
                          theme.palette.success.main,
                          0.3
                        )}`,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.success.dark,
                          textAlign: "center",
                        }}
                      >
                        {plan.transactionLimit}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.success.dark,
                          textAlign: "center",
                          display: "block",
                        }}
                      >
                        Valid for 12 months
                      </Typography>
                    </Box>

                    {/* CTA Button */}
                    <Button
                      fullWidth
                      variant={plan.popular ? "contained" : "outlined"}
                      size="large"
                      onClick={() => handleSubscribe(plan)}
                      disabled={loading}
                      sx={{
                        py: 1.5,
                        mb: 3,
                        fontWeight: 600,
                        borderRadius: 2,
                        textTransform: "none",
                        fontSize: "1rem",
                        ...(plan.popular && {
                          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                          "&:hover": {
                            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                          },
                        }),
                      }}
                    >
                      {loading && selectedPlan === plan.name ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        "Select Plan"
                      )}
                    </Button>

                    <Divider sx={{ mb: 3 }} />

                    {/* Features List */}
                    <List dense sx={{ "& .MuiListItem-root": { px: 0 } }}>
                      {plan.features.map((feature, idx) => (
                        <ListItem key={idx} sx={{ py: 0.75 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckCircleIcon
                              sx={{
                                color: theme.palette.success.main,
                                fontSize: 20,
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={feature}
                            primaryTypographyProps={{
                              variant: "body2",
                              color: "text.primary",
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Bottom CTA Section */}
          <Box
            sx={{
              mt: 8,
              p: 4,
              borderRadius: 3,
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              textAlign: "center",
            }}
          >
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              Need a custom solution?
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Contact our team for enterprise pricing and custom integrations
              tailored to your business needs.
            </Typography>
            <Button
              variant="outlined"
              size="large"
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 600,
                textTransform: "none",
              }}
            >
              Contact Sales
            </Button>
          </Box>
        </Container>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default SubscriptionPlan;